"use client";

import Link from "next/link";
import { BrandLogo } from "@/components/BrandLogo";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import { FaInstagram, FaViber, FaWhatsapp, FaEnvelope } from "react-icons/fa6";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import {
  isAuthMobileBottomNavPath,
  isServiceDetailPath,
  mobileBottomNavFooterInsetClassName,
} from "@/lib/mobile-bottom-nav";

const YEAR = new Date().getFullYear();

const MOBILE_HOME_NAV_PREF_KEY = "planner-mobile-home-nav-enabled";

/** Same routes as Navigation `showMobileBottomNav` — fixed bottom bar on mobile when logged in. */
const PLANNER_MOBILE_BOTTOM_ROUTES = [
  "/profil",
  "/spisak-gostiju",
  "/kalukator-budzeta",
  "/zadaci",
  "/podesavanja",
] as const;

const NAV = [
  { label: "Početna", href: "/" },
  { label: "Usluge", href: "/#usluge" },
  { label: "Objavi uslugu", href: "/#objavi-uslugu" },
  { label: "Profil", href: "/profil" },
  { label: "Gosti", href: "/spisak-gostiju" },
  { label: "Budžet", href: "/kalukator-budzeta" },
  { label: "Zadaci", href: "/zadaci" },
  { label: "Prijava", href: "/prijava" },
];

const SOCIAL = [
  { label: "Instagram", href: "https://instagram.com/proslava.ba", icon: FaInstagram },
  { label: "Viber", href: "viber://chat?number=%2B38766133034", icon: FaViber },
  { label: "WhatsApp", href: "https://wa.me/38766133034", icon: FaWhatsapp },
  { label: "Email", href: "mailto:kontakt@proslava.ba", icon: FaEnvelope },
];

export function Footer() {
  const pathname = usePathname();
  const { user } = useAuth();
  const [mobileHomeNavEnabled, setMobileHomeNavEnabled] = useState(true);

  const readPreference = useCallback(
    () =>
      typeof window !== "undefined" &&
      window.localStorage.getItem(MOBILE_HOME_NAV_PREF_KEY) !== "0",
    []
  );

  useEffect(() => {
    setMobileHomeNavEnabled(readPreference());

    const onPref = (event: Event | CustomEvent<{ enabled?: boolean }>) => {
      const detail = (event as CustomEvent<{ enabled?: boolean }>).detail;
      if (detail && typeof detail.enabled === "boolean") {
        setMobileHomeNavEnabled(detail.enabled);
        return;
      }
      setMobileHomeNavEnabled(readPreference());
    };

    const onStorage = (e: StorageEvent) => {
      if (e.key !== MOBILE_HOME_NAV_PREF_KEY) return;
      setMobileHomeNavEnabled(readPreference());
    };

    window.addEventListener(
      "planner:mobile-nav-preference",
      onPref as EventListener
    );
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(
        "planner:mobile-nav-preference",
        onPref as EventListener
      );
      window.removeEventListener("storage", onStorage);
    };
  }, [readPreference]);

  const padForHomeMobileNav =
    pathname === "/" && mobileHomeNavEnabled;

  const padForAuthMobileNav =
    isAuthMobileBottomNavPath(pathname) && mobileHomeNavEnabled;

  const padForServiceDetailMobileNav =
    isServiceDetailPath(pathname) && mobileHomeNavEnabled;

  const padForPlannerMobileBottomNav =
    Boolean(user) &&
    PLANNER_MOBILE_BOTTOM_ROUTES.some((route) => pathname === route);

  /** Same `max-lg` bottom inset as homepage above the mobile dock (home strip or planner bar). */
  const footerMobileBottomPad =
    padForHomeMobileNav ||
    padForAuthMobileNav ||
    padForServiceDetailMobileNav ||
    padForPlannerMobileBottomNav;

  return (
    <footer
      className={cn(
        "border-t border-border bg-card",
        footerMobileBottomPad && mobileBottomNavFooterInsetClassName,
      )}
    >
      <div className="mx-auto w-full max-w-[min(100%,96rem)] px-3">

        {/* ── Mobile: centered stack; Desktop: single row ── */}
        <div className="flex flex-col items-center gap-4 py-6 md:flex-row md:justify-between md:py-5">

          {/* Brand */}
          <Link href="/" className="inline-block shrink-0 py-1 transition-opacity hover:opacity-90">
            <BrandLogo />
          </Link>

          {/* Nav links — 4-col grid on mobile, inline row on desktop */}
          <nav className="grid grid-cols-4 gap-x-4 gap-y-4 text-center md:flex md:flex-wrap md:items-center md:gap-x-5 md:gap-y-2 md:text-left">
            {NAV.map(({ label, href }) => (
              <Link
                key={href + label}
                href={href}
                className="text-xs font-medium text-foreground/70 transition-colors duration-200 hover:text-foreground dark:text-muted-foreground dark:hover:text-foreground"
              >
                {label}
              </Link>
            ))}
          </nav>

          {/* Social / contact — same as ServiceSocialLinks on service cards */}
          <div className="flex flex-wrap items-center justify-center gap-2 md:justify-start">
            {SOCIAL.map(({ label, href, icon: Icon }) => (
              <a
                key={label}
                href={href}
                target={href.startsWith("http") ? "_blank" : undefined}
                rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
                aria-label={label}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border bg-background text-foreground transition-colors hover:bg-accent"
              >
                <Icon className="h-5 w-5" />
              </a>
            ))}
          </div>
        </div>

        {/* ── Bottom bar ── */}
        <div className="border-t border-border/60 py-3">
          <p className="text-center text-[11px] text-muted-foreground">
            © {YEAR} proslava.ba
          </p>
        </div>

      </div>
    </footer>
  );
}
